import { Sequence } from "domain/entities/techniques.entities";
import { HiOutlineTrash } from "react-icons/hi";
import { Dispatch, SetStateAction, useMemo } from "react";
import { INInput } from "ui/pages/app/components/inputs/INInput";
import { translate } from "ui/i18n";
import {
  BoldItalicUnderlineToggles,
  CreateLink,
  ListsToggle,
  MDXEditor,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { useWindowWidth } from "ui/utils/hooks";
import { Snackbar } from "ui/pages/snackbar";
import { INTimeInput } from "ui/pages/app/components/inputs/INTimeInput";
import { formatHref, formatMarkdownLinksHref } from "ui/utils/formatters";

interface IProps {
  sequence: Sequence & { deleted: boolean };
  index: number;
  sequencesLength: number;
  sequences: (Sequence & { deleted: boolean })[];
  setSequences: Dispatch<
    SetStateAction<
      (Sequence & {
        deleted: boolean;
      })[]
    >
  >;
}

export function EditSequenceComponent({
  sequence,
  index,
  sequencesLength,
  sequences,
  setSequences,
}: IProps) {
  const isDesktop = useWindowWidth();

  const mdxPlugins = useMemo(
    () => [
      headingsPlugin(),
      linkPlugin(),
      listsPlugin(),
      linkDialogPlugin({
        onClickLinkCallback: (url) => {
          window.open(formatHref(url), "_blank", "noopener,noreferrer");
        },
      }),
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <BoldItalicUnderlineToggles />
            <CreateLink />
            <ListsToggle options={["bullet", "number"]} />
          </>
        ),
      }),
    ],
    []
  );

  const handleUpdateSequence = (
    type: "deleted" | "title" | "storyline",
    value: string | boolean
  ) => {
    //if we delete a sequence, check if all other ones are in deleted state and don't change anything if so
    if (type === "deleted" && value === true) {
      const allOtherDeleted = sequences.every(
        (seq) => seq.id === sequence.id || seq.deleted
      );
      if (allOtherDeleted) {
        return Snackbar.showError({
          title: translate("ns1:Technique.AtLeastOneSequence"),
        });
      }
    }
    const updatedSequences = sequences.map((seq) => {
      if (seq.id === sequence.id) {
        return {
          ...seq,
          [type]: value,
        };
      }
      return seq;
    });
    setSequences(updatedSequences);
  };

  const updateSequenceDuration = (value: string) => {
    const updatedSequences = sequences.map((seq) => {
      if (seq.id === sequence.id) {
        return {
          ...seq,
          duration: value,
        };
      }
      return seq;
    });
    setSequences(updatedSequences);
  };

  return (
    <div
      className={`flex flex-col space-y-6 rounded-[20px] bg-darkGrey-100 px-4 py-5 md:flex-row ${
        index !== 0 ? "mt-6 md:mt-8" : ""
      }`}
    >
      <div
        className={`flex ${
          sequencesLength > 1 ? "justify-between" : "justify-end"
        }`}
      >
        {sequencesLength > 1 && (
          <div className="h-full w-14">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-2xl font-bold text-secondaryGrey-800">
              {index + 1}
            </span>
          </div>
        )}
        {!isDesktop && sequencesLength > 1 && (
          <div className="flex w-full justify-end">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={() => handleUpdateSequence("deleted", true)}
            >
              <HiOutlineTrash size={20} className="text-error" />
            </button>
          </div>
        )}
      </div>

      <div
        className={`flex w-full flex-col space-y-4 md:!mt-0 md:ml-4 ${
          sequencesLength > 1 ? "!mt-4" : "!mt-0"
        }`}
      >
        <div className="items-top flex justify-between">
          <INInput
            className="w-full md:w-[30%]"
            label={translate("ns1:Input.Labels.Title")}
            value={sequence.title}
            onChange={(value) => handleUpdateSequence("title", value)}
          />
          {isDesktop && sequencesLength > 1 && (
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={() => handleUpdateSequence("deleted", true)}
            >
              <HiOutlineTrash size={20} className="text-error" />
            </button>
          )}
        </div>
        <div className="space-y-2">
          <label className="w-full text-sm font-bold leading-6 text-darkGrey-700">
            {translate("ns1:Input.Labels.Duration")}
          </label>
          <INTimeInput
            setFormattedTiming={updateSequenceDuration}
            defaultValue={sequence.duration}
          />
        </div>
        <div className="space-y-2">
          <label className="!mt-6 w-full text-sm font-bold leading-6 text-darkGrey-700">
            {translate("ns1:Input.Labels.Storyline")}
          </label>
          <MDXEditor
            markdown={formatMarkdownLinksHref(sequence.storyline)}
            plugins={mdxPlugins}
            onChange={(value) =>
              handleUpdateSequence("storyline", formatMarkdownLinksHref(value))
            }
            className="rounded-xl border border-darkGrey-500 bg-white p-3 text-sm font-medium text-darkGrey-700"
          />
        </div>
      </div>
    </div>
  );
}
